import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PlayCircle, Compass, Home, Users, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { tourRegistry, TourMetadata } from "@/config/tours";

interface TourSelectionCardProps {
  userType: 'buyer' | 'seller';
}

// Group tours into categories
const getTourCategories = (userType: 'buyer' | 'seller') => {
  const tours = tourRegistry[userType];
  
  if (userType === 'buyer') {
    return [
      {
        id: 'getting-started',
        name: 'Getting Started',
        icon: Compass,
        tours: tours.filter(t => t.id === 'buyer_dashboard'),
      },
      {
        id: 'properties',
        name: 'Finding Properties',
        icon: Home,
        tours: tours.filter(t => ['buyer_search', 'buyer_property_detail', 'buyer_saved'].includes(t.id)),
      },
      {
        id: 'connections',
        name: 'Working with Sellers',
        icon: Users,
        tours: tours.filter(t => ['buyer_offers', 'buyer_sellers'].includes(t.id)),
      },
      {
        id: 'account',
        name: 'Account & Settings',
        icon: Settings,
        tours: tours.filter(t => ['buyer_notifications', 'buyer_profile', 'buyer_upgrade'].includes(t.id)),
      },
    ];
  }
  
  return [
    {
      id: 'getting-started',
      name: 'Getting Started',
      icon: Compass,
      tours: tours.filter(t => t.id === 'seller_dashboard'),
    },
    {
      id: 'properties',
      name: 'Managing Properties',
      icon: Home,
      tours: tours.filter(t => ['seller_new_property', 'seller_active_deals', 'seller_offers'].includes(t.id)),
    },
    {
      id: 'connections',
      name: 'Finding Buyers',
      icon: Users,
      tours: tours.filter(t => ['seller_search_buyers', 'seller_buyer_lists'].includes(t.id)),
    },
    {
      id: 'account',
      name: 'Account & Settings',
      icon: Settings,
      tours: tours.filter(t => ['seller_notifications', 'seller_profile', 'seller_upgrade'].includes(t.id)),
    },
  ];
};

const TourSelectionCard = ({ userType }: TourSelectionCardProps) => {
  const navigate = useNavigate();
  const categories = getTourCategories(userType);
  
  const handleStartTour = (tour: TourMetadata) => {
    navigate(tour.path, { state: { startTour: tour.id } });
  };
  
  return (
    <Card className="bg-godyspo-gold/10 border-godyspo-gold/20">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-godyspo-gold/20 flex items-center justify-center">
            <PlayCircle className="h-5 w-5 text-godyspo-gold" />
          </div>
          <div>
            <CardTitle className="text-lg">Platform Tours</CardTitle>
            <p className="text-sm text-muted-foreground">
              Learn how to use each feature
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Accordion type="single" collapsible className="w-full">
          {categories.map((category) => (
            <AccordionItem key={category.id} value={category.id} className="border-godyspo-gold/10">
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex items-center gap-2">
                  <category.icon className="h-4 w-4 text-godyspo-gold" />
                  <span className="text-sm font-medium">{category.name}</span>
                  <span className="text-xs text-muted-foreground ml-1">
                    ({category.tours.length})
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pl-6">
                  {category.tours.map((tour) => (
                    <div
                      key={tour.id}
                      className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
                    >
                      <div className="flex-1 min-w-0 mr-2">
                        <p className="text-sm font-medium truncate">{tour.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          {tour.description}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStartTour(tour)}
                        className="shrink-0 h-7 px-2 text-xs border-godyspo-gold/30 hover:bg-godyspo-gold/10 hover:text-godyspo-gold"
                      >
                        <PlayCircle className="h-3 w-3 mr-1" />
                        Start
                      </Button>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default TourSelectionCard;
